import { Box, CodeBlock as BaseCodeBlock, CopyButton, Flex, isReactElement, Select, Tabs, Text } from '@workos-inc/design-system';
import * as React from 'react';
import { useAnalytics } from 'src/utils/analytics-context';
import { LANGUAGE_META, REFERENCE_LANGUAGES } from 'src/utils/constants';
import { ReferenceLanguage } from 'src/utils/reference-schema';
import { useCodeBlockLanguage } from './code-block-language-provider';
import { WithReferenceDefinition } from './reference-definition';
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;
type Language = (typeof LANGUAGE_META)[number]['key'];
interface CodeBlockProps {
  children?: React.ReactNode;
  title?: string;
  referenceId?: string;
  file?: string;
}
interface CodeSnippet {
  children?: React.ReactNode;
  /** The text displayed in the code block header */
  title?: string;
  /** The corresponding language in the language selector, e.g. 'laravel' or 'curl' */
  language?: Language;
  /** The language used for syntax highlighting, e.g. 'php' or 'bash' (See https://prismjs.com/#supported-languages) */
  syntax?: string;
  /** Indicates that this code snippet is rendered in a tab. The prop is passed as `value` to Tabs component. */
  tab?: string;
}
export const CodeBlock = ({
  children,
  referenceId,
  title
}: CodeBlockProps) => {
  // CodeBlock should receive children as `pre` elements with extra props
  if (!childrenAreValid(children)) {
    throw Error('Invalid CodeBlock children');
  }
  const snippets: CodeSnippet[] = React.Children.map(children, pre => ({
    children: isReactElement<{
      children: React.ReactNode;
    }>(pre.props.children, ['children']) ? pre.props.children.props.children : null,
    title: pre.props.title ?? title,
    language: pre.props.language,
    syntax: pre.props.syntax,
    tab: pre.props.tab
  }));
  const [preferredLanguage, setPreferredLanguage] = useCodeBlockLanguage();
  const [activeSnippet, setActiveSnippet] = React.useState(() => getBestSnippet(snippets, undefined, preferredLanguage));
  const offsetRef = React.useRef<number>(undefined);
  const rafRef = React.useRef<number>(0);
  const codeBlockRef = React.useRef<HTMLDivElement>(null);

  // When changing the code block language, other code blocks will also change.
  // This means that the amount of content above the current code block may shift
  // and push the current code block around the viewport, which feels quite jarring.
  // We'll pin scroll position to the current code block so it looks like it doesn't move.
  useIsomorphicLayoutEffect(() => {
    const pinScroll = () => {
      const targetOffset = offsetRef.current;
      const currentOffset = codeBlockRef.current?.getBoundingClientRect().y;
      if (targetOffset === undefined || currentOffset === undefined) {
        return;
      }
      if (targetOffset !== currentOffset) {
        window.scrollBy({
          top: currentOffset - targetOffset
        });
      }

      // Keep updating the scroll position before every repaint
      rafRef.current = requestAnimationFrame(pinScroll);
    };
    if (!rafRef.current && offsetRef.current !== undefined) {
      // Start pinning the scroll...
      rafRef.current = requestAnimationFrame(pinScroll);
      // ...until all code blocks finish re-rendering and the main stack is empty
      setTimeout(() => {
        pinScroll();
        offsetRef.current = undefined;
        rafRef.current = 0;
      });
    }
    return () => cancelAnimationFrame(rafRef.current);
  });
  const analytics = useAnalytics();
  const trackCopyEvent = React.useCallback(async () => {
    await analytics.track('User copied code', {
      identifier: activeSnippet?.title,
      language: activeSnippet?.language
    });
  }, [analytics, activeSnippet]);

  // Languages available in the current tab
  const languageSelectSnippets = getSnippetsForLanguageSelect(snippets, activeSnippet);

  // Pick a better snippet whenever there is one in the preferred language. This will be needed for the
  // subsequent side-effect that makes the _other_ code blocks react to a language change in a code block.
  const bestSnippet = getBestSnippet(languageSelectSnippets, activeSnippet, preferredLanguage);

  // Update the active snippet synchronously so that when the
  // preferred language changes, all code blocks change in tandem
  useIsomorphicLayoutEffect(() => {
    if (!equal(activeSnippet, bestSnippet)) {
      setActiveSnippet(bestSnippet);
    }
  }, [activeSnippet, bestSnippet]);
  if (!activeSnippet) {
    return null;
  }
  const tabs = getTabs(snippets, activeSnippet, preferredLanguage);
  const languagesCount = getUniqueLanguages(languageSelectSnippets).length;
  const renderHeader = languagesCount > 1 || activeSnippet.title;

  // Render children instead of Tab parts when there are no tabs
  const TabsRoot = activeSnippet.tab ? Tabs.Root : Children;
  const TabsList = activeSnippet.tab ? Tabs.List : Children;
  const TabsTrigger = activeSnippet.tab ? Tabs.Trigger : Children;
  const TabsContent = activeSnippet.tab ? Tabs.Content : Children;

  // If there are tabs, always use the first tab's language as the second
  // tab tends to be a JSON response, and we don't want to change languages
  // when it is active. Otherwise, default to the current snippet's language.
  const language = tabs?.[0]?.language ?? activeSnippet.language;
  return <WithReferenceDefinition language={getReferenceLanguage(language)} referenceId={referenceId} data-sentry-element="WithReferenceDefinition" data-sentry-component="CodeBlock" data-sentry-source-file="code-block.tsx">
      <TabsRoot value={activeSnippet.tab} onValueChange={newTab => {
      const newSnippet = tabs?.find(({
        tab
      }) => tab === newTab);
      setActiveSnippet(newSnippet);
    }} data-sentry-element="TabsRoot" data-sentry-source-file="code-block.tsx">
        <BaseCodeBlock.Root ref={codeBlockRef} data-algolia-exclude data-sentry-element="BaseCodeBlock.Root" data-sentry-source-file="code-block.tsx">
          {renderHeader && <BaseCodeBlock.Header>
              <Box mr="4">
                {!tabs && activeSnippet.title && <Text size="2" weight="bold">
                    {activeSnippet.title}
                  </Text>}

                {tabs && tabs.length > 1 && <TabsList>
                    {tabs.map((snippet, i) => <TabsTrigger key={snippet?.tab ?? snippet?.language ?? i} value={snippet?.tab ?? snippet?.language ?? String(i)}>
                        {snippet?.title}
                      </TabsTrigger>)}
                  </TabsList>}
              </Box>

              <Flex align="center" gap="2">
                {activeSnippet.language && languagesCount === 1 && <Text color="gray" size="1">
                    {getLanguageName(activeSnippet.language)}
                  </Text>}

                {activeSnippet.language && languagesCount > 1 && <Select.Root size="1" value={activeSnippet.language} onValueChange={(newLanguage: Language) => {
              const newSnippet = snippets.find(({
                language
              }) => language === newLanguage);
              if (newSnippet && newSnippet.language) {
                offsetRef.current = codeBlockRef.current?.getBoundingClientRect().y;
                setActiveSnippet(newSnippet);
                setPreferredLanguage(newSnippet.language);
              }
            }}>
                    <Select.Trigger ghost mr="1" />
                    <Select.Content>
                      {getUniqueLanguages(languageSelectSnippets).map(({
                  language,
                  syntax
                }) => <Select.Item key={language} value={language}>
                            {getLanguageName(language, syntax)}
                          </Select.Item>)}
                    </Select.Content>
                  </Select.Root>}

                <CopyButton onClick={() => {
              const linesContainer = activeSnippet.tab ? codeBlockRef.current?.querySelector('[role="tabpanel"][data-state="active"]') : codeBlockRef.current;
              const lines = linesContainer?.querySelectorAll('.CodeBlockLine') || [];
              const codeBlockLines = Array.from(lines);
              const code = codeBlockLines.map(node => `${node.textContent}`).join('');
              void navigator.clipboard.writeText(code);
              void trackCopyEvent();
            }} />
              </Flex>
            </BaseCodeBlock.Header>}

          {(tabs ?? [activeSnippet]).map((snippet, i) => <TabsContent key={snippet?.tab ?? snippet?.language ?? i} value={snippet?.tab ?? snippet?.language ?? String(i)}>
              {snippet?.tab === activeSnippet.tab && <BaseCodeBlock.Content data-language={snippet?.syntax} wrap={snippet?.syntax === 'url' ? 'pre-wrap' : 'pre'} onCopy={() => void trackCopyEvent()}>
                  {snippet?.children}
                </BaseCodeBlock.Content>}
            </TabsContent>)}
        </BaseCodeBlock.Root>
      </TabsRoot>
    </WithReferenceDefinition>;
};
type CodeBlockChild = React.ReactElement<HTMLPreElement> & {
  type: 'pre';
  props: CodeSnippet & {
    children: React.ReactElement<HTMLElement> & {
      type: 'code';
    };
  };
};

// Valid Code Block children are <pre> elements with a single <code> child
const childrenAreValid = (children: React.ReactNode): children is CodeBlockChild | CodeBlockChild[] => React.Children.toArray(children).every(child => isReactElement<{
  children: React.ReactNode;
}>(child, ['children']) && React.Children.only(child.props.children) && child.type === 'pre');
const Children = ({
  children
}: {
  children?: React.ReactNode;
}) => <>{children}</>;

/**
 * Get snippets that you should be able to switch between using
 * the language select (without switching tabs, if they exist).
 */
const getSnippetsForLanguageSelect = (snippets: CodeSnippet[], activeSnippet: CodeSnippet | undefined) => {
  // Assume the code block might have no tabs or some language-specific tabs. In this case,
  // the list of languages doesn’t change across tabs, so we can use all snippets available.
  let snippetsToUse = snippets;

  // However, if the code block has tabs across all languages, the list
  // of languages is what's available within the current tab only.
  if (activeSnippet?.tab && activeSnippet.tab.match(/^\d/)) {
    snippetsToUse = snippets.filter(({
      tab
    }) => tab === activeSnippet.tab);
  }
  return snippetsToUse;
};

/** Get a list of unique languages and their syntax for a given snippet list */
const getUniqueLanguages = (snippets: CodeSnippet[]) =>
// Introducing lint rule banning type assertions
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
snippets.reduce((accumulator, currentValue) => {
  if (accumulator.some(({
    language
  }) => language === currentValue.language)) {
    return accumulator;
  } else {
    return [...accumulator, {
      language: currentValue.language,
      syntax: currentValue.syntax
    }];
  }
}, [{
  language: snippets[0]?.language,
  syntax: snippets[0]?.syntax
}]).filter(({
  language
}) => Boolean(language)) as {
  language: Language;
  syntax?: string;
}[];

/**
 * Get snippets that you can switch between using tabs (without using the language select first).
 * That is your current snippet (if it is in a tab) and snippets in parallel tabs.
 */
const getTabs = (snippets: CodeSnippet[], activeSnippet: CodeSnippet, preferredLanguage: Language) => {
  if (!activeSnippet.tab) {
    return null;
  }
  let snippetsToUse: CodeSnippet[] = [];
  if (activeSnippet.tab.match(/^\d/)) {
    // If the code block has tabs across all languages, the list of tabs corresponds
    // to the list of snippets on the other tab plus the current snippet.
    snippets.forEach(snippet => {
      if (equal(snippet, activeSnippet)) {
        snippetsToUse.push(activeSnippet);
      }
      const isThisTab = snippet.tab === activeSnippet.tab;

      // We don't care about other snippets in the current tab
      if (isThisTab) {
        return;
      }
      const index = snippetsToUse.findIndex(({
        tab
      }) => tab === snippet.tab);
      if (index === -1) {
        // Record the snippet if there's none for this tab
        snippetsToUse.push(snippet);
      } else if (snippet.language === preferredLanguage) {
        // Replace the snippet for a tab if this one uses the preferred language
        snippetsToUse[index] = snippet;
      }
    });
  } else {
    // If the code block has language-speciic tabs, the list
    // of tabs will use a common prefix for the tab name
    const prefix = activeSnippet.tab.replace(/\d+$/, '');
    snippetsToUse = snippets.filter(({
      tab
    }) => tab?.startsWith(prefix));
  }
  return snippetsToUse;
};

/**
 * Pick the best snippet to show based on the available
 * snippets, currently active snippet, and the preferred language
 */
const getBestSnippet = (snippets: CodeSnippet[], activeSnippet: CodeSnippet | undefined, preferredLanguage: Language) => {
  // Prioritize the currently active snippet if it is in the preferred language
  if (activeSnippet?.language === preferredLanguage) {
    return activeSnippet;
  }

  // Otherwise, prioritize the first snippet in the preferred language
  let preferredLanguageSnippet = snippets.find(({
    language
  }) => language === preferredLanguage);

  // PHP will do if Laravel is preferred
  if (preferredLanguage === 'laravel' && !preferredLanguageSnippet) {
    preferredLanguageSnippet = snippets.find(({
      language
    }) => language === 'php');
  }
  return preferredLanguageSnippet ?? activeSnippet ?? snippets[0];
};
const getLanguageName = (languageKey: string, syntax?: string) => {
  // When user wants to see cURL snippets, but the file syntax is JSON,
  // display "JSON" in the language selector instead of "cURL".
  if (languageKey === 'curl' && syntax === 'json') {
    return 'JSON';
  }
  return LANGUAGE_META.find(({
    key
  }) => key === languageKey)?.name;
};
const getReferenceLanguage = (language?: Language) => {
  if (language === 'laravel') {
    return 'php';
  }

  // Introducing lint rule banning type assertions
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (REFERENCE_LANGUAGES.includes(language as ReferenceLanguage)) {
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return language as ReferenceLanguage;
  }
  return undefined;
};
const equal = (a: CodeSnippet | undefined, b: CodeSnippet | undefined) => a?.language === b?.language && a?.tab === b?.tab;